import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Calendar, CheckCircle2, X, Users, ArrowRight, Activity, TrendingUp } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { fmtDate, fmtTime, StatusBadge } from '../../lib/format';
import { useAuth } from '../../lib/auth';
import SpecialistDashboardWidget from '../../components/SpecialistDashboardWidget';

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl bg-white p-6 shadow-[0_15px_40px_-25px_rgba(20,17,15,0.15)] border border-neutral-100">
      <div className="flex items-center justify-between">
        <div className={`w-11 h-11 rounded-lg ${accent} flex items-center justify-center`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
        <TrendingUp className="w-4 h-4 text-emerald-500" />
      </div>
      <p className="mt-4 text-[32px] font-extrabold text-[#14110f] leading-none">{value}</p>
      <p className="text-[12.5px] text-neutral-500 mt-1.5">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const isSpecialist = user && ['tax_specialist', 'advisor'].includes(user.role);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) { setData({ skipped: true }); return; }
    api.dashboard().then(setData).catch(() => setData({ error: true }));
  }, [isAdmin]);

  if (!data) return <AdminLayout title="Dashboard"><div className="text-neutral-400">Loading…</div></AdminLayout>;

  // Specialists see only their dedicated widget (no admin tiles).
  if (isSpecialist && !isAdmin) {
    return (
      <AdminLayout title="Dashboard" subtitle="Your week at a glance">
        <SpecialistDashboardWidget />
        <div className="rounded-xl bg-white border border-neutral-100 p-6 text-[13px] text-neutral-500 text-center">
          Use the sidebar to access your clients, appointments, documents and profile.
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard" subtitle="Today's operations at a glance">
      {!data || data.error || data.skipped ? (
        <div className="rounded-xl bg-white border border-neutral-100 p-6 text-[13px] text-neutral-500">
          Dashboard data is unavailable for your role.
        </div>
      ) : (
      <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Calendar} label="Today’s appointments" value={data.todays_count} accent="bg-[#f5b300]/15 text-[#c89200]" />
        <Stat icon={ClipboardList} label="Upcoming" value={data.upcoming_count} accent="bg-blue-50 text-blue-600" />
        <Stat icon={Users} label="Total clients" value={data.clients_total} accent="bg-emerald-50 text-emerald-600" />
        <Stat icon={X} label="Cancelled (30d)" value={data.cancelled_30d} accent="bg-rose-50 text-rose-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl bg-white p-6 border border-neutral-100">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[17px] font-bold text-[#14110f]">Upcoming this week</h3>
            <Link to="/admin/appointments" className="text-[12.5px] text-[#c89200] hover:underline flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          {data.next_appointments.length === 0 ? (
            <div className="py-12 text-center text-neutral-400 text-[13px]">No upcoming appointments.</div>
          ) : (
            <table className="w-full text-[13.5px]">
              <thead className="text-[11px] uppercase tracking-wider text-neutral-400">
                <tr><th className="py-2 text-left">When</th><th className="py-2 text-left">Service</th><th className="py-2 text-left">Client</th><th className="py-2 text-right">Status</th></tr>
              </thead>
              <tbody>
                {data.next_appointments.map((a) => (
                  <tr key={a.id} className="border-t border-neutral-100">
                    <td className="py-3">
                      <Link to={`/admin/appointments/${a.id}`} className="hover:text-[#c89200] font-medium">{fmtDate(a.start)}</Link>
                    </td>
                    <td className="py-3 text-neutral-600">{a.appointment_type_name}</td>
                    <td className="py-3 text-neutral-600">{a.client_first_name} {a.client_last_name}</td>
                    <td className="py-3 text-right"><StatusBadge status={a.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-xl bg-white p-6 border border-neutral-100">
          <h3 className="text-[17px] font-bold text-[#14110f] mb-5">Recent activity</h3>
          <ul className="space-y-4">
            {data.recent_activity.slice(0, 6).map((a) => (
              <li key={a.id} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#f5b300]/15 flex items-center justify-center shrink-0">
                  <Activity className="w-4 h-4 text-[#c89200]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-[#14110f] truncate">{a.client_first_name} {a.client_last_name} booked {a.appointment_type_name}</p>
                  <p className="text-[11.5px] text-neutral-400">{fmtDate(a.created_at, undefined, { hour: undefined, minute: undefined })}</p>
                </div>
                <StatusBadge status={a.status} />
              </li>
            ))}
          </ul>
        </div>
      </div>
      </>
      )}
    </AdminLayout>
  );
}
